import { Checkbox, FormControl, InputLabel, TextField } from "@mui/material";
import { SelectField } from "../ExploreArtists/style";
import { useStyles } from "../MyTracks/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import React, { useContext, useEffect, useState } from "react";
import { FileObj, MyFilesFiltersProps } from "./types";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { TrackFileType, TrackFileTypeMap } from "../../proto/api_pb";
import { StyledCardAdd } from "../../components/TrackComponents/TrackMenuButton/styles";
import CloseIcon from "@mui/icons-material/Close";
import { APP_COLOR, SVG_ICON_COLOR } from "../../theme";
import {
  SessionSvg,
  StemVocalSvg,
  StemDrumSvg,
  StemMelodySvg,
  StemInstrumentSvg,
  StemBasslineSvg,
  LyricsSvg,
  ArtworkSvg,
  OtherSvg,
} from "../../components/SVGIcons";
import { AuthContext } from "../../contexts";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface SvgMapInerface {
  [key: string]: JSX.Element;
}
const svgMap: SvgMapInerface = {
  SESSION: <SessionSvg color={SVG_ICON_COLOR} />,
  "STEM VOCAL": <StemVocalSvg color={SVG_ICON_COLOR} />,
  "STEM DRUM": <StemDrumSvg color={SVG_ICON_COLOR} />,
  "STEM MELODY": <StemMelodySvg color={SVG_ICON_COLOR} />,
  "STEM BASSLINE": <StemBasslineSvg color={SVG_ICON_COLOR} />,
  "STEM INSTRUMENT": <StemInstrumentSvg color={SVG_ICON_COLOR} />,
  LYRICS: <LyricsSvg color={SVG_ICON_COLOR} />,
  ARTWORK: <ArtworkSvg color={SVG_ICON_COLOR} />,
  OTHER: <OtherSvg color={SVG_ICON_COLOR} />,
};

const trackFileTypeOptions = Object.keys(TrackFileType)
  .filter(
    (key) =>
      TrackFileType[key as keyof TrackFileTypeMap] !==
      TrackFileType.TRACK_FILE_TYPE_UNSPECIFIED
  )
  .map((key) => {
    const parts = key.split("_");
    let lastName = "";
    if (parts[parts.length - 2] === "STEM")
      lastName = parts[parts.length - 2] + " " + parts[parts.length - 1];
    else lastName = parts[parts.length - 1];

    return {
      label: lastName,
      value: TrackFileType[key as keyof TrackFileTypeMap],
    };
  });

export const MyFilesFilters: React.FC<MyFilesFiltersProps> = ({
  files,
  setSelectedFiles,
  selectedFiles,
}) => {
  const { userId } = useContext(AuthContext);
  const [collaboratorsList, setCollaboratorsList] = useState<string[]>([]);
  const [selectedFileType, seSelectedFileType] = useState<string>("");
  const [filteredCollabsList, setFilteredCollabsList] = useState<FileObj[]>([]);
  const [filteredFileTypeList, setFilteredFileTypeList] = useState<FileObj[]>(
    []
  );
  const [isAllFiltersEmpty, setIsAllFiltersEmpty] = useState(true);
  const [collaboratorsOptionsList, setCollaboratorsOptionsList] = useState<
    string[]
  >([]);
  const classes = useStyles();

  const handleChangeCollaboratorsList = async (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const values = value as Array<string>;
    const tempArr: string[] = [];
    const length = values.length;
    for (let i = 0; i < length; i++) {
      const tag_curr = values[i];
      tempArr.push(tag_curr);
    }
    setCollaboratorsList([...tempArr]);
    const filteredFiles = files.filter((file) => {
      const collaborators = file.trackFile.getCollaboratorsList();
      const collaboratorUsernames = collaborators.map((collaborator) =>
        collaborator.getUsername()
      );
      return collaboratorUsernames.some((username) =>
        tempArr.includes(username)
      );
    });
    setFilteredCollabsList([...filteredFiles]);

    // filtering logic:
    // if both filters are empty just show all files
    // else if we clear collabs filter just go with file type filter (not empty)
    // if collabs filter not empty find intersection (if file type filter not empty)

    if (values.length === 0 && filteredFileTypeList.length === 0) {
      const totalFilter = [...files];
      setSelectedFiles(totalFilter);
      setIsAllFiltersEmpty(true);
    } else if (values.length === 0 && filteredFileTypeList.length !== 0) {
      const totalFilter = [...filteredFileTypeList];
      setSelectedFiles(totalFilter);
      setIsAllFiltersEmpty(false);
    } else {
      const totalFilter =
        filteredFileTypeList.length > 0
          ? filteredFiles.filter((item1) =>
              filteredFileTypeList.some(
                (item2) => item2.trackFile.getId() === item1.trackFile.getId()
              )
            )
          : [...filteredFiles];
      setSelectedFiles(totalFilter);
      setIsAllFiltersEmpty(false);
    }
  };

  const handleChangeFileType = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    seSelectedFileType(value);
    const filteredFiles = files.filter((file, _i) => {
      return file.trackFile.getType() === +value;
    });
    setFilteredFileTypeList([...filteredFiles]);

    // find intersection of collabs are filtered
    // if not just keep the file type filter
    const totalFilter =
      filteredCollabsList.length > 0
        ? filteredFiles.filter((item1) =>
            filteredCollabsList.some(
              (item2) => item2.trackFile.getId() === item1.trackFile.getId()
            )
          )
        : [...filteredFiles];

    setSelectedFiles(totalFilter);

    setIsAllFiltersEmpty(false);
  };

  const handleClearFilter = () => {
    setCollaboratorsList([]);
    setFilteredCollabsList([]);
    setFilteredFileTypeList([]);
    seSelectedFileType("");
    setIsAllFiltersEmpty(true);
    setSelectedFiles(files);
  };

  const getUniqueCollaboratorsNames = (files: FileObj[]) => {
    const uniqueNamesSet = new Set<string>();
    files.forEach((file) => {
      file.trackFile
        .getCollaboratorsList()
        .filter((collab) => collab.getUserId() !== userId)
        .forEach((collaborator) => {
          uniqueNamesSet.add(collaborator.getUsername());
        });
    });
    return Array.from(uniqueNamesSet);
  };

  useEffect(() => {
    const uniqueCollaboratorsNamesList = getUniqueCollaboratorsNames(files);
    setCollaboratorsOptionsList(uniqueCollaboratorsNamesList);
    // eslint-disable-next-line
  }, [collaboratorsList.length, files, selectedFileType]);

  return (
    <>
      <StyledCardAdd
        disabled={isAllFiltersEmpty}
        onClick={handleClearFilter}
        sx={{
          marginRight: "2px",
          color: isAllFiltersEmpty ? "grey" : "black",
          backgroundColor: isAllFiltersEmpty ? "rgba(0,0,0,0)" : APP_COLOR,
          "&:hover": {
            backgroundColor: isAllFiltersEmpty ? "rgba(0,0,0,0)" : APP_COLOR,
          },
        }}
      >
        <CloseIcon /> Clear
      </StyledCardAdd>
      <FormControl sx={{ borderColor: "white" }} id="step-4">
        <InputLabel id="select-label" sx={{ fontFamily: "Argumentum" }}>
          {collaboratorsList.length > 0 ? "" : "Collaborators"}
        </InputLabel>
        <SelectField
          multiple
          style={{ width: "250px" }}
          id="collaborator-tags"
          options={collaboratorsOptionsList}
          disableCloseOnSelect
          value={collaboratorsList}
          onChange={handleChangeCollaboratorsList}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontFamily: "Argumentum" }}>
              <>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              sx={{ width: "250px" }}
              {...params}
              label={""}
              InputLabelProps={{ shrink: false }}
              classes={{ root: classes.customTextField }}
            />
          )}
        />
      </FormControl>
      <FormControl>
        <InputLabel id="filter-type">File Type</InputLabel>
        <Select
          style={{ width: "250px" }}
          labelId="filter-type"
          value={selectedFileType}
          label="File Type"
          sx={{
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: APP_COLOR,
            },
          }}
          onChange={handleChangeFileType}
        >
          {trackFileTypeOptions.map((option) => (
            <MenuItem
              sx={{ backgroundColor: "inherit" }}
              key={option.value}
              value={option.value}
            >
              {svgMap[option.label]}&nbsp;&nbsp;
              {option.label[0].toUpperCase() +
                option.label.slice(1).toLowerCase()}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};
